/**
 * Load a short list of Google scripts in CORS mode so a throw inside them is REPORTED, not redacted.
 *
 * WHY. When a script from another origin throws, the browser hands `window.onerror` only "Script error." (no file, line, column or
 * stack) unless that script was fetched with `crossorigin`. error_groups 6d4edc24b87fc369 holds 109 such events (09-09..10-24): 108 were
 * masked, every one had maps.googleapis.com on the page and 107 had the map overlay mounted, and the vis.gl APIProvider (1.8.1) and the
 * Places fallback in AddressAutocomplete both inject the Maps script WITHOUT `crossorigin`. With CORS mode the same throw arrives with its
 * real message, file and stack and takes the normal same-origin path in globalHandlers.ts (a new, honest fingerprint).
 *
 * THE RISK AND HOW IT IS BOUNDED. `crossorigin` on a script whose server sends no Access-Control-Allow-Origin makes the script FAIL TO
 * LOAD, which would take Maps down. There is deliberately NO runtime retry: a retry cannot be made invisible to the callers that hang
 * `onload`/`onerror` properties on the original element (vis.gl, AddressAutocomplete), so it would turn a CORS failure into a half-working
 * page. The risk is bounded up front, on two axes:
 *   1. WHICH SCRIPT, by exact URL path: ONLY the Maps bootstrap, https://maps.googleapis.com/maps/api/js. Its CORS answer was measured
 *      with curl and an Origin header (2026-10-25). Nothing else is matched. In particular NOT apis.google.com/js/api.js: it is the
 *      loader for the Firebase Google sign-in popup on every origin, and with no fallback a header-stripping proxy or a Google header
 *      change would break SIGN-IN for everyone, while the error group this targets (6d4edc24b87fc369) is Maps. Also NOT the Maps JSONP
 *      data endpoints under /maps/api/js/... (CORS never measured).
 *      🔴 CORRECTED 2026-10-28: the bootstrap is NOT where the throws live. It loads the library as further scripts under
 *      /maps-api-v3/api/js/<ver>/ (main.js, common.js, util.js, map.js, marker.js), and those answer 200 with NO
 *      Access-Control-Allow-Origin (measured with curl, Origin technician.onpointgen.com, version 66/11a; the bootstrap in the same run did
 *      echo it). So they must never be put in CORS mode (it would stop Maps loading), and the events in 6d4edc24b87fc369 stay masked
 *      after this change (5 of 5 on release bc11237a53). The masked path records page visibility instead (pageVisibilityClock.ts).
 *   2. WHICH PAGE ORIGIN, from a short list: https://app.onpointgen.com, https://technician.onpointgen.com, http://localhost:3000. THIS LIST
 *      IS A DELIBERATE SCOPE LIMIT, NOT A MEASURED BOUNDARY: Google answers the Maps bootstrap with the caller's own origin for ANY origin
 *      (it echoed time.onpointgen.com, sales.onpointgen.com and www.example.com too), so the list does not mark where CORS stops working.
 *      It keeps the change to the two apps that raised the error group; the time app (time.onpointgen.com) and the sales app
 *      (sales.onpointgen.com) are unchanged, as is any preview channel or new subdomain.
 * Residual risk, named: a header-stripping proxy or a Google-side change to the Maps bootstrap's CORS answer would stop Maps from loading
 * on the listed origins until this call is removed. Roll back = delete the call in errorLogging/index.ts (no stored state).
 *
 * NOT covered, by design: the static pages under client/public/forms/ (they inject Maps from their own HTML) and scripts made by the HTML
 * parser or innerHTML.
 *
 * HOW. The attribute has to be on the element BEFORE the browser starts the fetch, which happens when `src` is set and the element is
 * inserted. Both loaders assign `script.src` (or setAttribute), so the `src` property setter and `setAttribute('src')` of
 * HTMLScriptElement are wrapped. A `crossOrigin` already chosen by the caller is kept. Idempotent (a second install returns a no-op); the
 * returned cleanup restores the original behaviour exactly and is a no-op for a stale handle. Never throws: a telemetry aid must not be
 * able to break script loading.
 */

const TRUSTED_CORS_SCRIPTS: ReadonlyArray<{ readonly host: string; readonly path: string }> = [
  { host: 'maps.googleapis.com', path: '/maps/api/js' },
];

/** Page origins this change is limited to (a scope limit, not a measured boundary; see the header). */
const MEASURED_PAGE_ORIGINS: ReadonlySet<string> = new Set([
  'https://app.onpointgen.com',
  'https://technician.onpointgen.com',
  'http://localhost:3000',
]);

/** True when `src` is an https URL on the measured-CORS list. */
export function isTrustedCorsScript(src: unknown): boolean {
  try {
    if (typeof src !== 'string' || !src) return false;
    const url = new URL(src, window.location.href);
    return url.protocol === 'https:' && TRUSTED_CORS_SCRIPTS.some((t) => url.hostname === t.host && url.pathname === t.path);
  } catch {
    return false;
  }
}

/** True when the page itself is on one of the listed origins. */
export function isMeasuredPageOrigin(origin: unknown): boolean {
  return typeof origin === 'string' && MEASURED_PAGE_ORIGINS.has(origin);
}

let installed: (() => void) | null = null;

export function installTrustedScriptCors(getPageOrigin: () => string = () => window.location.origin): () => void {
  if (installed || typeof HTMLScriptElement === 'undefined') return () => {};

  const proto = HTMLScriptElement.prototype;
  const srcDesc = Object.getOwnPropertyDescriptor(proto, 'src');
  const ownSetAttribute = Object.getOwnPropertyDescriptor(proto, 'setAttribute'); // usually undefined: inherited from Element
  const originalSetAttribute = proto.setAttribute;
  if (!srcDesc || !srcDesc.set || !srcDesc.get) return () => {};

  const mark = (el: HTMLScriptElement, value: unknown): void => {
    try {
      if (!el.hasAttribute('crossorigin') && isMeasuredPageOrigin(getPageOrigin()) && isTrustedCorsScript(value)) {
        originalSetAttribute.call(el, 'crossorigin', 'anonymous');
      }
    } catch {
      /* never let telemetry break script loading */
    }
  };

  Object.defineProperty(proto, 'src', {
    configurable: true,
    enumerable: srcDesc.enumerable,
    get: srcDesc.get,
    set(this: HTMLScriptElement, value: string) {
      mark(this, value);
      srcDesc.set!.call(this, value);
    },
  });
  proto.setAttribute = function patchedSetAttribute(this: HTMLScriptElement, name: string, value: string) {
    if (typeof name === 'string' && name.toLowerCase() === 'src') mark(this, value);
    return originalSetAttribute.call(this, name, value);
  };

  const cleanup = () => {
    if (installed !== cleanup) return; // a stale handle must not undo a newer install
    Object.defineProperty(proto, 'src', srcDesc);
    if (ownSetAttribute) Object.defineProperty(proto, 'setAttribute', ownSetAttribute);
    else delete (proto as unknown as Record<string, unknown>).setAttribute;
    installed = null;
  };
  installed = cleanup;
  return cleanup;
}
